/**
 * Annotations on the open document (worker/src/annotations.js): highlights, page notes and drawings with their
 * comment threads, shared with the team when it has shared notes, else only yours. One document at a time (the
 * viewer's); a teammate's change arrives as the window event "team:annotations" (overview/live.ts) and refetches.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { toastStore } from "../../stores/toastStore";

export type AnnotationKind = "highlight" | "note" | "ink";
export type AnnotationColor = "yellow" | "green" | "blue" | "red";
export interface Rect { x: number; y: number; w: number; h: number }
export type AnnotationData = { rects: Rect[] } | { x: number; y: number } | { paths: number[][]; width: number };
export interface AnnotationComment { id: string; body: string; author: { id: string; name: string }; mine: boolean; createdAt: string }
export interface Annotation {
  id: string; doc: string; page: number; kind: AnnotationKind; color: AnnotationColor; data: AnnotationData;
  quote: string | null; body: string | null; author: { id: string; name: string }; mine: boolean; shared: boolean;
  createdAt: string; updatedAt: string; comments: AnnotationComment[];
}
export type NewAnnotation = Pick<Annotation, "page" | "kind" | "color" | "data"> & { quote?: string | null; body?: string | null };

interface State {
  doc: string | null;
  items: Annotation[];
  /** The annotation open in the side panel (its thread), or null. */
  selected: string | null;
  load: (doc: string) => Promise<void>;
  add: (a: NewAnnotation) => Promise<Annotation | null>;
  update: (id: string, patch: { body?: string | null; color?: AnnotationColor }) => Promise<void>;
  remove: (id: string) => Promise<void>;
  reply: (id: string, body: string) => Promise<boolean>;
  removeComment: (id: string, commentId: string) => Promise<void>;
  select: (id: string | null) => void;
}

const API = "/api/dashboard/annotations";
async function call<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) throw new Error(String(res.status));
  return res.json() as Promise<T>;
}
const failed = (message: string) => toastStore.getState().show({ message });

export const annotationsStore = createStore<State>()((set, get) => ({
  doc: null,
  items: [],
  selected: null,
  load: async (doc) => {
    if (get().doc !== doc) set({ doc, items: [], selected: null });
    const r = await call<{ annotations: Annotation[] }>(`${API}?doc=${encodeURIComponent(doc)}`).catch(() => null);
    if (r && get().doc === doc) set({ items: r.annotations });
  },
  add: async (a) => {
    const doc = get().doc;
    if (!doc) return null;
    const saved = await call<Annotation>(API, "POST", { doc, ...a }).catch(() => null);
    if (!saved) { failed("Couldn't save that. Try again."); return null; }
    if (get().doc === doc) set({ items: [...get().items, saved].sort((x, y) => x.page - y.page || x.createdAt.localeCompare(y.createdAt)), selected: saved.id });
    return saved;
  },
  update: async (id, patch) => {
    const prev = get().items;
    set({ items: prev.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
    const saved = await call<Annotation>(`${API}/${encodeURIComponent(id)}`, "PATCH", patch).catch(() => null);
    if (!saved) { set({ items: prev }); failed("Couldn't save that. Try again."); return; }
    set({ items: get().items.map((a) => (a.id === id ? { ...saved, comments: a.comments } : a)) });
  },
  remove: async (id) => {
    const prev = get().items;
    set({ items: prev.filter((a) => a.id !== id), selected: get().selected === id ? null : get().selected });
    const ok = await call(`${API}/${encodeURIComponent(id)}`, "DELETE").then(() => true, () => false);
    if (!ok) { set({ items: prev }); failed("Couldn't delete that. Try again."); }
  },
  reply: async (id, body) => {
    const c = await call<AnnotationComment>(`${API}/${encodeURIComponent(id)}/comments`, "POST", { body }).catch(() => null);
    if (!c) { failed("Couldn't post your reply. Try again."); return false; }
    set({ items: get().items.map((a) => (a.id === id ? { ...a, comments: [...a.comments, c] } : a)) });
    return true;
  },
  removeComment: async (id, commentId) => {
    const prev = get().items;
    set({ items: prev.map((a) => (a.id === id ? { ...a, comments: a.comments.filter((c) => c.id !== commentId) } : a)) });
    const ok = await call(`${API}/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`, "DELETE").then(() => true, () => false);
    if (!ok) { set({ items: prev }); failed("Couldn't delete that. Try again."); }
  },
  select: (selected) => set({ selected }),
}));

export const useAnnotations = <T,>(selector: (s: State) => T) => useStore(annotationsStore, selector);

/** The document's key for annotations: its URL path on our site (an archived /files/… copy), else the full URL. */
export function docKeyOf(url: string) {
  try {
    const u = new URL(url, location.href);
    return u.origin === location.origin ? u.pathname : u.origin + u.pathname;
  } catch { return url; }
}
